// ============================================================================
// TIP DEALER MODAL COMPONENT
// ============================================================================
// Full-featured tip modal with currency selection, amount presets,
// and beautiful animations
//  PRODUCTION: Dual currency support (PKCash / PlayCoins)

//  ENTERPRISE: iOS WebView compatible, non-blocking operations

//  LEGAL: User explicitly chooses which currency to use

// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import tipDealerService, {
  TipCurrency,
  TipAmount,
  TIP_AMOUNTS_PKCASH,
  TIP_AMOUNTS_PLAYCOINS,
} from '../services/tipDealerService';
import voiceAnnouncer from '../utils/VoiceAnnouncer';
import './TipDealerModal.css';

// ============================================================================
// PROPS INTERFACE
// ============================================================================

interface TipDealerModalProps {
  isOpen: boolean;
  onClose: () => void;
  tableId: string;
  currentUserId: string;
  currentUserName: string;
  tableCurrency?: TipCurrency; // Default to table's currency
  dealerImageUrl?: string;
  onBalanceChange?: (currency: TipCurrency, newBalance: number) => void;
  voiceEnabled?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

const TipDealerModal: React.FC<TipDealerModalProps> = ({
  isOpen,
  onClose,
  tableId,
  currentUserId,
  currentUserName,
  tableCurrency = 'playcoins',
  dealerImageUrl,
  onBalanceChange,
  voiceEnabled = true,
}) => {
  // ============================================================================
  // STATE
  // ============================================================================
  
  const [selectedCurrency, setSelectedCurrency] = useState<TipCurrency>(tableCurrency);
  const [selectedAmount, setSelectedAmount] = useState<TipAmount | null>(null);
  const [customAmount, setCustomAmount] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);
  
  // Balances
  const [playCoinsBalance, setPlayCoinsBalance] = useState(0);
  const [pkCashBalance, setPkCashBalance] = useState(0);
  const [isLoadingBalances, setIsLoadingBalances] = useState(true);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const tipAmounts = useMemo(() => {
    return selectedCurrency === 'pkcash' ? TIP_AMOUNTS_PKCASH : TIP_AMOUNTS_PLAYCOINS;
  }, [selectedCurrency]);

  const currentBalance = useMemo(() => {
    return selectedCurrency === 'pkcash' ? pkCashBalance : playCoinsBalance;
  }, [selectedCurrency, pkCashBalance, playCoinsBalance]);

  const finalAmount = useMemo(() => {
    if (selectedAmount) return selectedAmount.value;
    const parsed = parseFloat(customAmount);
    return isNaN(parsed) ? 0 : parsed;
  }, [selectedAmount, customAmount]);

  const canAfford = useMemo(() => {
    return finalAmount > 0 && currentBalance >= finalAmount;
  }, [finalAmount, currentBalance]);

  const formattedBalance = useMemo(() => {
    if (selectedCurrency === 'pkcash') {
      return `$${currentBalance.toFixed(2)}`;
    }
    return currentBalance >= 1000 
      ? `${(currentBalance / 1000).toFixed(1)}K` 
      : currentBalance.toLocaleString();
  }, [selectedCurrency, currentBalance]);

  const formattedAmount = useMemo(() => {
    if (finalAmount <= 0) return '';
    if (selectedCurrency === 'pkcash') {
      return `$${finalAmount.toFixed(2)}`;
    }
    return finalAmount >= 1000 
      ? `${(finalAmount / 1000).toFixed(finalAmount % 1000 === 0 ? 0 : 1)}K` 
      : finalAmount.toLocaleString();
  }, [selectedCurrency, finalAmount]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Load balances when modal opens
  useEffect(() => {
    if (!isOpen || !currentUserId) return;

    const loadBalances = async () => {
      setIsLoadingBalances(true);
      try {
        const balances = await tipDealerService.getBothBalances(currentUserId);
        setPlayCoinsBalance(balances.playCoins);
        setPkCashBalance(balances.pkCash);
      } catch (err) {
        console.error('Error loading balances:', err);
      } finally {
        setIsLoadingBalances(false);
      }
    };

    loadBalances();
  }, [isOpen, currentUserId]);

  // Update cooldown timer
  useEffect(() => {
    if (!isOpen || !currentUserId) return;

    const updateCooldown = () => {
      const remaining = tipDealerService.getCooldownRemaining(currentUserId);
      setCooldown(remaining);
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [isOpen, currentUserId]);

  // Clear messages after delay
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedAmount(null);
      setCustomAmount('');
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  // Reset selection when currency changes
  useEffect(() => {
    setSelectedAmount(null);
    setCustomAmount('');
  }, [selectedCurrency]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleCurrencyChange = useCallback((currency: TipCurrency) => {
    setSelectedCurrency(currency);
    setError(null);
  }, []);

  const handleAmountSelect = useCallback((amount: TipAmount) => {
    setSelectedAmount(amount);
    setCustomAmount('');
    setError(null);
  }, []);

  const handleCustomAmountChange = useCallback((value: string) => {
    // Only allow valid number input
    const cleaned = value.replace(/[^0-9.]/g, '');
    
    // Prevent multiple decimal points
    const parts = cleaned.split('.');
    if (parts.length > 2) return;
    
    // Limit decimal places based on currency
    if (parts[1] && parts[1].length > (selectedCurrency === 'pkcash' ? 2 : 0)) return;
    
    setCustomAmount(cleaned);
    setSelectedAmount(null);
    setError(null);
  }, [selectedCurrency]);

  const handleTip = useCallback(async () => {
    if (!canAfford || isProcessing || cooldown > 0) return;

    setIsProcessing(true);
    setError(null);
    setSuccess(null);

    try {
      // Add timeout for iOS WebView (15 seconds)
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout - please try again')), 15000);
      });

      const tipPromise = tipDealerService.tipDealer(
        tableId,
        currentUserId,
        currentUserName,
        finalAmount,
        selectedCurrency
      );

      const result = await Promise.race([tipPromise, timeoutPromise]);

      if (result.success) {
        // Update local balance
        if (selectedCurrency === 'pkcash') {
          setPkCashBalance(result.newBalance || 0);
        } else {
          setPlayCoinsBalance(result.newBalance || 0);
        }

        // Notify parent of balance change
        if (onBalanceChange && result.newBalance !== undefined) {
          onBalanceChange(selectedCurrency, result.newBalance);
        }

        // Voice announcement
        if (voiceEnabled) {
          voiceAnnouncer.announceDealerTip(currentUserName, finalAmount, selectedCurrency);
        }

        // Show success message
        const emoji = selectedAmount?.emoji || '';

        setSuccess(`${emoji} Tipped ${formattedAmount}! Thank you!`);

        // Reset selection
        setSelectedAmount(null);
        setCustomAmount('');

        // Close modal after brief delay to show success
        setTimeout(() => {
          onClose();
        }, 1500);

      } else {
        setError(result.error || 'Failed to process tip');
      }

    } catch (err: any) {
      console.error('❌ [TipDealerModal] Error:', err);
      setError(err.message || 'Failed to process tip - please try again');
    } finally {
      setIsProcessing(false);
    }
  }, [
    canAfford,
    isProcessing,
    cooldown,
    tableId,
    currentUserId,
    currentUserName,
    finalAmount,
    selectedCurrency,
    selectedAmount,
    formattedAmount,
    voiceEnabled,
    onBalanceChange,
    onClose,
  ]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!isOpen) return null;

  return (
    <div className="tip-dealer-overlay" onClick={onClose}>
      <div className="tip-dealer-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tip-dealer-header">
          <h3> Tip the Dealer</h3>

          <button className="tip-dealer-close" onClick={onClose}></button>

        </div>

        {/* Dealer Image (if premium theme) */}
        {dealerImageUrl && (
          <div className="tip-dealer-image-container">
            <img 
              src={dealerImageUrl} 
              alt="Dealer" 
              className="tip-dealer-image"
            />
            <div className="tip-dealer-image-glow" />
          </div>
        )}

        {/* Currency Selection */}
        <div className="tip-currency-section">
          <label className="tip-section-label">Select Currency</label>
          <div className="tip-currency-toggle">
            <button
              className={`tip-currency-btn ${selectedCurrency === 'playcoins' ? 'active' : ''}`}
              onClick={() => handleCurrencyChange('playcoins')}
            >
              <span className="currency-icon"></span>

              <span className="currency-name">PlayCoins</span>
              <span className="currency-balance">
                {isLoadingBalances ? '...' : playCoinsBalance.toLocaleString()}
              </span>
            </button>
            <button
              className={`tip-currency-btn ${selectedCurrency === 'pkcash' ? 'active'  : ''}`}
              onClick={() => handleCurrencyChange('pkcash')}
            >
              <span className="currency-icon"></span>

              <span className="currency-name">PSS Coins</span>
              <span className="currency-balance">
                {isLoadingBalances ? '...' : `$${pkCashBalance.toFixed(2)}`}
              </span>
            </button>
          </div>
        </div>

        {/* Amount Selection */}
        <div className="tip-amount-section">
          <label className="tip-section-label">
            Select Amount
            <span className="tip-balance-label">
              Balance: {formattedBalance}
            </span>
          </label>
          
          <div className="tip-amounts-grid">
            {tipAmounts.map((tip) => {
              const canAffordThis = currentBalance >= tip.value;
              const isSelected = selectedAmount?.value === tip.value;
              
              return (
                <button
                  key={tip.value}
                  className={`tip-amount-btn ${isSelected ? 'selected' : ''} ${!canAffordThis ? 'disabled' : ''}`}
                  onClick={() => canAffordThis && handleAmountSelect(tip)}
                  disabled={!canAffordThis}
                  title={tip.description}
                >
                  <span className="tip-emoji">{tip.emoji}</span>
                  <span className="tip-value">{tip.label}</span>
                  {!canAffordThis && (
                    <span className="tip-insufficient">Insufficient</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom Amount */}
        <div className="tip-custom-section">
          <label className="tip-section-label">Or Enter Custom Amount</label>
          <div className="tip-custom-input-wrapper">
            {selectedCurrency === 'pkcash' && (
              <span className="tip-currency-prefix">$</span>
            )}
            <input
              type="text"
              inputMode="decimal"
              className="tip-custom-input"
              placeholder={selectedCurrency === 'pkcash' ? '0.00' : '0'}
              value={customAmount}
              onChange={(e) => handleCustomAmountChange(e.target.value)}
              disabled={isProcessing}
            />
            {selectedCurrency === 'playcoins' && (
              <span className="tip-currency-suffix">PC</span>
            )}
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="tip-message tip-error">
            <span className="tip-message-icon"></span>

            {error}
          </div>
        )}
        {success && (
          <div className="tip-message tip-success">
            <span className="tip-message-icon"></span>

            {success}
          </div>
        )}

        {/* Action Button */}
        <div className="tip-action-section">
          {cooldown > 0 ? (
            <button className="tip-action-btn cooldown" disabled>
              ⏳ Wait {cooldown}s
            </button>
          ) : (
            <button
              className={`tip-action-btn ${canAfford && finalAmount > 0 ? 'ready' : 'disabled'}`}
              onClick={handleTip}
              disabled={!canAfford || finalAmount <= 0 || isProcessing}
            >
              {isProcessing ? (
                <>
                  <span className="tip-spinner" />
                  Processing...
                </>
              ) : finalAmount > 0 ? (
                <>
                   Tip {formattedAmount}

                </>
              ) : (
                'Select an amount'
              )}
            </button>
          )}
        </div>

        {/* Legal Disclaimer */}
        <div className="tip-disclaimer">
          <p>
            Tips are voluntary and non-refundable. 
            By tipping, you confirm you are at least 18 years old.
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TIP ANIMATION OVERLAY COMPONENT
// ============================================================================
// Shows flying coins/money animation when someone tips

interface TipAnimationOverlayProps {
  isVisible: boolean;
  tipperName: string;
  amount: number;
  currency: TipCurrency;
  emoji: string;
  onComplete?: () => void;
}

export const TipAnimationOverlay: React.FC<TipAnimationOverlayProps> = ({
  isVisible,
  tipperName,
  amount,
  currency,
  emoji,
  onComplete,
}) => {
  const [showingAnimation, setShowingAnimation] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShowingAnimation(true);
      
      // Auto-hide after animation duration
      const timer = setTimeout(() => {
        setShowingAnimation(false);
        onComplete?.();
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, onComplete]);

  if (!showingAnimation) return null;

  const formattedAmount = currency === 'pkcash' 
    ? `$${amount.toFixed(2)}` 
    : `${amount.toLocaleString()} PC`;

  return (
    <div className="tip-animation-overlay">
      {/* Flying coins/money particles */}
      <div className="tip-particles">
        {[...Array(12)].map((_, i) => (
          <div 
            key={i} 
            className={`tip-particle tip-particle-${i}`}
            style={{
              '--delay': `${i * 0.1}s`,
              '--x-offset': `${(Math.random() - 0.5) * 200}px`,
              '--y-offset': `${Math.random() * -150 - 50}px`,
              '--rotation': `${Math.random() * 720}deg`,
            } as React.CSSProperties}
          >
            {emoji}
          </div>
        ))}
      </div>

      {/* Tip announcement */}
      <div className="tip-announcement">
        <div className="tip-announcement-icon">{emoji}</div>
        <div className="tip-announcement-text">
          <span className="tip-announcement-name">{tipperName}</span>
          <span className="tip-announcement-action">tipped the dealer</span>
          <span className="tip-announcement-amount">{formattedAmount}</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// TIP DEALER BUTTON COMPONENT
// ============================================================================
// Small button that appears near the dealer to trigger tip modal

interface TipDealerButtonProps {
  onClick: () => void;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  showLabel?: boolean;
}

export const TipDealerButton: React.FC<TipDealerButtonProps> = ({
  onClick,
  disabled = false,
  size = 'medium',
  showLabel = true,
}) => {
  return (
    <button
      className={`tip-dealer-btn tip-dealer-btn-${size} ${disabled ? 'disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
      title="Tip the Dealer"
    >
      <span className="tip-dealer-btn-icon"></span>

      {showLabel && <span className="tip-dealer-btn-label">Tip</span>}
    </button>
  );
};

export default TipDealerModal;